import { Button, Card, CardContent, CardTitle, EmptyState, Skeleton, StatusBadge } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { PresentationStatus, SaleSummary } from '@bia-auction/shared-schema';
import { saleSummarySchema } from '@bia-auction/shared-schema';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Gavel, Lock, Timer } from 'lucide-react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMsg } from '../components/Feedback';
import { PageHero } from '../components/PageHero';
import { ShowcaseCounters } from '../components/ShowcaseCounters';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCountdown, formatDate } from '../lib/format';
import { presentationLabel } from '../lib/labels';
import { PHOTOS } from '../lib/photos';
import { useNow } from '../lib/useNow';

/** Public payloads carry only the masked presentationStatus — map it onto the §6 palette
 * (done = CLOSED → success tint per the §6 table; neutral is for draft/unsold). */
function presentationVariant(s: PresentationStatus) {
  if (s === 'inprogress') return 'live' as const;
  if (s === 'preparing') return 'info' as const;
  if (s === 'cancelled') return 'destructive' as const;
  if (s === 'done') return 'success' as const;
  return 'secondary' as const;
}

/**
 * The site's front page (M7, redesigned B1) — a gradient hero band, the marketing counters, and
 * for a **signed-in member** the auction list from `GET /auctions`: a single column of
 * full-width summary cards, because few auctions run concurrently and a 3-up grid left most of
 * the row empty.
 *
 * **A logged-out visitor gets no auctions at all** ([ADR 0027](../../../../docs/adr/0027-login-gated-auction-surface.md)).
 * Through M18 this list was public — it named every auction, its dates, its vehicle count and
 * its status, and carried thumbnails of the cars — and the 13.08.2026 product decision closed
 * that. What is left above the gate is the hero and the counters, which are claims about the
 * *place* and name no auction, so the page still says what Bia is to someone who arrives cold.
 *
 * The list query is `enabled` on the session rather than merely hidden: an unauthenticated
 * `GET /auctions` is a 401, and firing it on every visit would spend a request to render an
 * error nobody sees. The API is still the authority — this is the polite half of the gate.
 */
export function ShowcasePage() {
  const { state } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['auctions'],
    enabled: state.authenticated,
    queryFn: () => api<SaleSummary[]>('/auctions', {}, z.array(saleSummarySchema)),
    // The list has no realtime subscription, so poll: a member who leaves it open still sees
    // status transitions (preparing → in-progress → done) and newly-listed auctions without a
    // manual reload. useNow() drives the countdown between polls.
    refetchInterval: 30_000,
  });
  const now = useNow();

  // **Every** read of the protected payload goes through this, not through `data`.
  // `enabled: false` stops the next *request*; it does not drop the last response, and TanStack
  // Query keeps serving it from cache. On logout — or a refresh rejection that clears the session
  // out of band — `state.authenticated` flips synchronously (`useSyncExternalStore` over the token
  // store) while the shell's `qc.clear()` + remount runs in a post-render effect, so there is one
  // committed frame in between. Reading `data` in that frame put a live auction count in the hero
  // subline above an already-rendered sign-in gate. Gating the subline alone would fix this
  // instance; gating the value fixes the class, and any field added below inherits it.
  const sales = state.authenticated ? data : undefined;

  return (
    <div className="space-y-6">
      <PageHero
        photo={PHOTOS.yardAerial}
        title={t('bidder.showcase.title')}
        subline={
          sales && sales.length > 0
            ? t('bidder.showcase.subline', { n: sales.length })
            : undefined
        }
      />

      {/* Marketing totals (M16). Above the list because it is a claim about the place, not
          about any auction in it — and it renders nothing until an operator turns it on. It is
          also the one auction-adjacent figure a visitor may see: an aggregate names nobody. */}
      <ShowcaseCounters />

      {!state.authenticated ? (
        <SignInGate />
      ) : isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : error ? (
        <ErrorMsg error={error} />
      ) : !sales || sales.length === 0 ? (
        <EmptyState icon={Gavel} title={t('bidder.showcase.empty')} />
      ) : (
        <div className="space-y-4">
          {sales.map((sale) => (
            <ShowcaseCard key={sale.id} sale={sale} now={now} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What a logged-out visitor sees where the auction list would be.
 *
 * Deliberately **not** an error and not an empty state: nothing has failed and nothing is
 * missing — the visitor simply is not a member yet, and the page's job at that point is to say
 * so and offer the two ways forward. One primary action (docs/15): signing in is the path for
 * everyone who already has an account, and registering sits beside it as the outline variant.
 */
function SignInGate() {
  return (
    <EmptyState
      icon={Lock}
      title={t('bidder.showcase.membersOnly')}
      description={t('bidder.showcase.loginPrompt')}
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button asChild variant="brand" className="h-11">
            <Link to="/login">{t('bidder.nav.login')}</Link>
          </Button>
          <Button asChild variant="outline" className="h-11">
            <Link to="/register">{t('bidder.nav.register')}</Link>
          </Button>
        </div>
      }
    />
  );
}

/**
 * One metadata fact as a tinted chip — the group site's card vocabulary (M18).
 *
 * `success-tint` rather than a raw green: the tinted pair is theme-aware and already carries
 * the AA-checked text colour (docs/15 §6), so the chip stays legible if the surface under it
 * ever changes. The dot is decorative and purely rhythmic — it is what makes a row of chips
 * read as a set rather than as buttons.
 */
function MetaChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-success-tint px-2.5 py-1 text-xs font-medium text-success-tint-fg">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-success" />
      {children}
    </span>
  );
}

function ShowcaseCard({ sale, now }: { sale: SaleSummary; now: number }) {
  // Three distinct situations, and the card must never mix their vocabularies. A sale is
  // promoted to LIVE at its *listingDate*, so "LIVE" alone does not mean bidding is open —
  // `startDate` decides that (the API's presentationStatus now agrees, see sale-mapper).
  const finished = sale.presentationStatus === 'done' || sale.presentationStatus === 'cancelled';
  const upcoming = !finished && now < sale.startDate;
  const running = !finished && !upcoming;

  // A finished auction gets NO countdown: "Tamamlandı · Kalan 2g" is a contradiction, and it
  // was reachable whenever a sale closed before the startDate it was still counting toward.
  const remaining = finished ? 0 : (upcoming ? sale.startDate : sale.endDate) - now;

  return (
    <Link
      to="/auctions/$auctionId"
      params={{ auctionId: sale.id }}
      className="block rounded-lg transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Full-width row: identity on the left, the numbers right-aligned on ≥sm so the
          countdowns line up down the column; stacked on phones. The running auction is the
          one a visitor can act on, so it is physically larger and tinted. */}
      <Card className={running ? 'border-success bg-success-tint ring-1 ring-success' : undefined}>
        <CardContent
          className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${
            running ? 'p-7' : 'p-5'
          }`}
        >
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {/* `font-head` explicitly: CardTitle does not carry it, and docs/15's type
                  scale pairs H2 with Poppins — `text-h2` alone renders body font at 22px. */}
              <CardTitle className={running ? 'font-head text-h2' : 'text-base'}>
                {sale.title ?? t('sales.untitled')}
              </CardTitle>
              <StatusBadge variant={presentationVariant(sale.presentationStatus)}>
                {presentationLabel(sale.presentationStatus)}
              </StatusBadge>
            </div>
            {/* Chips, not a run of grey text (M18). The group site's listing cards carry the
                same facts this way — `● 2021 ● Benzin ● Otomatik` — and it is how brand green
                reaches their page dozens of times without a single large green surface. Each
                fact also becomes separately scannable, which the middot-joined line was not. */}
            <div className="flex flex-wrap items-center gap-1.5">
              {sale.location && <MetaChip>{sale.location}</MetaChip>}
              {/* Date **and time** — the day alone tells a bidder when to be free, not when
                  to be at the screen. */}
              <MetaChip>
                <span className="tabular-nums">
                  {formatDate(sale.startDate, { weekday: true, time: true })}
                </span>
              </MetaChip>
              <MetaChip>
                <span className="tabular-nums">
                  {t('bidder.showcase.lots', { n: sale.lotCount })}
                </span>
              </MetaChip>
            </div>
          </div>
          <div
            className={`flex shrink-0 items-center gap-6 text-muted-foreground ${
              running ? 'text-base' : 'text-sm'
            }`}
          >
            {remaining > 0 && (
              <span className="flex items-center gap-1 text-foreground">
                <Timer aria-hidden className="h-4 w-4" />
                <span className="whitespace-nowrap font-mono tabular-nums">
                  {/* "Kalan" only while it is actually running; an upcoming sale is counting
                      down to its START, which is a different sentence. */}
                  {upcoming ? t('bidder.showcase.startsIn') : t('bidder.showcase.remaining')}{' '}
                  {formatCountdown(remaining)}
                </span>
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
